import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { assessmentApi } from '@/services/learningManagementApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Edit, Trash2, ClipboardCheck } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Assessment } from '@/types/learningManagement';

export const AssessmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'assessment');
  const canDelete = hasPermission('delete', 'assessment');

  // Use entity hook for fetching assessment
  const {
    data: assessment,
    isLoading,
    error,
    refetch,
  } = useEntity<Assessment>({
    fetchFn: (assessmentId: string) => assessmentApi.getById(assessmentId),
    entityId: id,
    entityName: 'assessment',
    enabled: !!id,
  });

  const isSystemDefined = assessment?.assessmentGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'assessment'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Assessment ID is required');
      await assessmentApi.delete(id);
    },
    entityName: 'assessment',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/assessments';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this assessment?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading assessment..." />;
  }

  if (error || !assessment) {
    return <ErrorState error={error || new Error('Assessment not found')} onRetry={refetch} />;
  }

  const assessmentId = assessment.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/assessments">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              View Assessment
            </h2>
            <p className="text-muted-foreground mt-1">Viewing: {assessment.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/assessments/${assessmentId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Assessment identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={assessmentId} label="Assessment ID" />
                <div className="space-y-2">
                  <Label>Title</Label>
                  <p className="mt-1">{assessment.title}</p>
                </div>
              </div>

              {assessment.description && (
                <div className="space-y-2">
                  <Label>Description</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                    value={assessment.description}
                    disabled
                  />
                </div>
              )}

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>Assessment Type *</Label>
                  <Input value={assessment.assessmentType} disabled />
                </div>
                {assessment.category && (
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input value={assessment.category} disabled />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Input value={assessment.status} disabled />
                </div>
              </div>

              {assessment.difficultyLevel && (
                <div className="space-y-2">
                  <Label>Difficulty Level</Label>
                  <Input value={assessment.difficultyLevel} disabled />
                </div>
              )}

              {assessment.tags && assessment.tags.length > 0 && (
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {assessment.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Generation Source</Label>
                <Input value={assessment.assessmentGenerated} disabled />
              </div>

              {assessment.createdAt && (
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Created At</Label>
                    <Input value={new Date(assessment.createdAt).toLocaleString()} disabled />
                  </div>
                  {assessment.updatedAt && (
                    <div className="space-y-2">
                      <Label>Updated At</Label>
                      <Input value={new Date(assessment.updatedAt).toLocaleString()} disabled />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Assessment Settings</CardTitle>
              <CardDescription>Configuration and parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {assessment.totalQuestions !== undefined && (
                  <div className="space-y-2">
                    <Label>Total Questions</Label>
                    <Input value={assessment.totalQuestions.toString()} disabled />
                  </div>
                )}
                {assessment.timeLimitMinutes !== undefined && (
                  <div className="space-y-2">
                    <Label>Time Limit (Minutes)</Label>
                    <Input value={assessment.timeLimitMinutes.toString()} disabled />
                  </div>
                )}
                {assessment.passingScorePercentage !== undefined && (
                  <div className="space-y-2">
                    <Label>Passing Score (%)</Label>
                    <Input value={assessment.passingScorePercentage.toString()} disabled />
                  </div>
                )}
                {assessment.maxAttempts !== undefined && (
                  <div className="space-y-2">
                    <Label>Max Attempts</Label>
                    <Input value={assessment.maxAttempts.toString()} disabled />
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Randomize Questions</Label>
                  <Input value={assessment.randomizeQuestions ? 'Yes' : 'No'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Show Correct Answers</Label>
                  <Input value={assessment.showCorrectAnswers ? 'Yes' : 'No'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Immediate Feedback</Label>
                  <Input value={assessment.immediateFeedback ? 'Yes' : 'No'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Adaptive</Label>
                  <Input value={assessment.adaptive ? 'Yes' : 'No'} disabled />
                </div>
              </div>

              {assessment.prerequisites && assessment.prerequisites.length > 0 && (
                <div className="space-y-2">
                  <Label>Prerequisites</Label>
                  <div className="flex flex-wrap gap-2">
                    {assessment.prerequisites.map((prereq, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono"
                      >
                        {prereq}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {assessment.associatedLearningPaths && assessment.associatedLearningPaths.length > 0 && (
                <div className="space-y-2">
                  <Label>Associated Learning Paths</Label>
                  <div className="flex flex-wrap gap-2">
                    {assessment.associatedLearningPaths.map((path, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono"
                      >
                        {path}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssessmentDetailPage;






